import { useEffect, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';

interface Settings {
  customerId: string;
  btpEnabled: boolean;
  approvalRequired: boolean;
  savingsReport: boolean;
  defaultObt: string | null;
  notificationPref: string;
  updatedBy: string | null;
  updatedAt: string | null;
  hasRecord: boolean;
}

const OBT_OPTIONS = ['andavo', 'concur', 'certify', 'deem'];
const NOTIFICATION_OPTIONS = ['digest', 'realtime', 'off'];

export function SettingsPanel({ customerId }: { customerId: string }) {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [permErr, setPermErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiFetch<Settings>(
      `/api/customer-settings/${encodeURIComponent(customerId)}`,
    )
      .then((data) => {
        if (!cancelled) setSettings(data);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  const save = async (patch: Partial<Settings>) => {
    if (!settings) return;
    const prev = settings;
    const next = { ...settings, ...patch };
    setSettings(next);
    setSaving(true);
    setError(null);
    setPermErr(false);
    try {
      await apiFetch(
        `/api/customer-settings/${encodeURIComponent(customerId)}`,
        {
          method: 'PUT',
          body: JSON.stringify(patch),
        },
      );
    } catch (e) {
      const err = e instanceof ApiError ? e : null;
      if (err?.status === 403) {
        setPermErr(true);
        setSettings(prev);
      } else {
        setError(err?.message || String(e));
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner" />
        <div className="loading-text">Loading settings…</div>
      </div>
    );
  }
  if (error || !settings) {
    return (
      <div className="banner banner-error">
        {error || 'Could not load settings'}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 760 }}>
      {permErr && (
        <div className="banner banner-warn">
          You don&rsquo;t have permission to edit customer settings. Admin role
          required.
        </div>
      )}

      <div className="section">
        <div className="section-header">
          <span className="section-label">Program flags</span>
          <span className="section-meta">Customer-level feature toggles</span>
        </div>
        <div
          className="section-body"
          style={{ paddingTop: 4, paddingBottom: 4 }}
        >
          <SettingToggle
            label="Enrolled in Supplier Management"
            description="Records that this client participates in our Hotel Supplier Management program. Reporting only — the Hotel Supplier tab and the Sourcing Workspace show every client regardless of enrollment."
            checked={settings.btpEnabled}
            onChange={(v) => save({ btpEnabled: v })}
            disabled={saving}
          />
          <SettingToggle
            label="Approval required"
            description="RFP responses and rate decisions require client approval before activation."
            checked={settings.approvalRequired}
            onChange={(v) => save({ approvalRequired: v })}
            disabled={saving}
          />
          <SettingToggle
            label="Savings report"
            description="Generate a quarterly client-facing PDF savings report for this customer."
            checked={settings.savingsReport}
            onChange={(v) => save({ savingsReport: v })}
            disabled={saving}
          />
        </div>
      </div>

      <div className="section">
        <div className="section-header terra">
          <span className="section-label">Defaults</span>
          <span className="section-meta">Per-customer overrides</span>
        </div>
        <div
          className="section-body"
          style={{ paddingTop: 4, paddingBottom: 4 }}
        >
          <SettingSelect
            label="Default OBT"
            description="Which online booking tool this customer uses by default."
            value={settings.defaultObt || ''}
            options={['', ...OBT_OPTIONS]}
            onChange={(v) => save({ defaultObt: v || null })}
            disabled={saving}
          />
          <SettingSelect
            label="Notification preference"
            description="How updates about this customer reach their account team."
            value={settings.notificationPref}
            options={NOTIFICATION_OPTIONS}
            onChange={(v) => save({ notificationPref: v })}
            disabled={saving}
          />
        </div>
      </div>

      <div
        style={{
          fontSize: 'var(--fs-sm)',
          color: 'var(--text-faint)',
          padding: '8px 4px',
          letterSpacing: '0.02em',
        }}
      >
        Last updated{' '}
        {settings.updatedAt
          ? new Date(settings.updatedAt).toLocaleString()
          : '—'}{' '}
        by {settings.updatedBy || '—'}
        {!settings.hasRecord && (
          <span style={{ marginLeft: 6, fontStyle: 'italic' }}>
            (defaults shown; first edit creates a record)
          </span>
        )}
      </div>
    </div>
  );
}

function SettingRow({
  label,
  description,
  control,
}: {
  label: string;
  description: string;
  control: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 24,
        padding: '14px 0',
        borderBottom: '1px solid var(--border-light)',
      }}
    >
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: 'var(--fs-md)',
            color: 'var(--text-primary)',
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: 'var(--fs-base)',
            color: 'var(--text-muted)',
            marginTop: 3,
            lineHeight: 1.5,
          }}
        >
          {description}
        </div>
      </div>
      <div style={{ flexShrink: 0, minWidth: 60 }}>{control}</div>
    </div>
  );
}

function SettingToggle({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled: boolean;
}) {
  return (
    <SettingRow
      label={label}
      description={description}
      control={
        <button
          type="button"
          aria-pressed={checked}
          disabled={disabled}
          onClick={() => onChange(!checked)}
          className={
            'toggle' + (checked ? ' on' : '') + (disabled ? ' disabled' : '')
          }
        >
          <span className="thumb" />
        </button>
      }
    />
  );
}

function SettingSelect({
  label,
  description,
  value,
  options,
  onChange,
  disabled,
}: {
  label: string;
  description: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
  disabled: boolean;
}) {
  return (
    <SettingRow
      label={label}
      description={description}
      control={
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="select"
          style={{ minWidth: 140 }}
        >
          {options.map((o) => (
            <option key={o} value={o}>
              {o || '(none)'}
            </option>
          ))}
        </select>
      }
    />
  );
}
